import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { 
  MapPin, Upload, Trash2, Download, Calendar, Clock, 
  TrendingUp, Gauge, Mountain, Eye, Plus, Route, X,
  Navigation, Share2, Mail, MessageCircle, Copy, Check
} from 'lucide-react';
import { toast } from 'sonner';
import { MapContainer, TileLayer, Polyline, useMap } from 'react-leaflet';
import DynamicTileLayer from '@/components/DynamicTileLayer';
import 'leaflet/dist/leaflet.css';
import TrackRecorder from './TrackRecorder';

// Component to fit map bounds to track
function FitBounds({ positions }) {
  const map = useMap();
  
  useEffect(() => {
    if (positions && positions.length > 0) {
      const bounds = positions.map(p => [p.lat, p.lon]);
      map.fitBounds(bounds, { padding: [20, 20] });
    }
  }, [positions, map]);
  
  return null;
}

export default function UserTracksSection() {
  const [tracks, setTracks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showTrackModal, setShowTrackModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [selectedTrack, setSelectedTrack] = useState(null);
  const [shareTrack, setShareTrack] = useState(null);
  const [loadingTrack, setLoadingTrack] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [copied, setCopied] = useState(false);
  const fileInputRef = useRef(null);
  
  const [uploadForm, setUploadForm] = useState({
    name: '',
    date: new Date().toISOString().split('T')[0],
    description: '',
    file: null
  });

  useEffect(() => {
    fetchTracks();
  }, []);

  const fetchTracks = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/user/tracks`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTracks(response.data.tracks || []);
    } catch (error) {
      console.error('Error fetching tracks:', error);
      toast.error('Error al cargar los tracks');
    } finally {
      setLoading(false);
    }
  };

  const viewTrack = async (track) => {
    setLoadingTrack(true);
    setShowTrackModal(true);
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/user/tracks/${track.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedTrack(response.data);
    } catch (error) {
      console.error('Error fetching track details:', error);
      toast.error('Error al cargar el track');
      setShowTrackModal(false);
    } finally {
      setLoadingTrack(false);
    }
  };

  const uploadTrack = async () => {
    if (!uploadForm.name || !uploadForm.file) {
      toast.error('Nombre y archivo GPX son requeridos');
      return;
    }

    setUploading(true);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('name', uploadForm.name);
      formData.append('date', uploadForm.date);
      formData.append('description', uploadForm.description || '');
      formData.append('gpx_file', uploadForm.file);

      const response = await axios.post(`${API}/user/tracks`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      toast.success('Track guardado correctamente');
      setShowUploadModal(false);
      setUploadForm({ name: '', date: new Date().toISOString().split('T')[0], description: '', file: null });
      if (fileInputRef.current) fileInputRef.current.value = '';
      fetchTracks();
    } catch (error) {
      console.error('Error uploading track:', error);
      toast.error(error.response?.data?.detail || 'Error al subir el track');
    } finally {
      setUploading(false);
    }
  };

  const deleteTrack = async (trackId) => {
    if (!confirm('¿Estás seguro de eliminar este track?')) return;

    setDeleting(trackId);
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/user/tracks/${trackId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Track eliminado');
      setTracks(prev => prev.filter(t => t.id !== trackId));
    } catch (error) {
      console.error('Error deleting track:', error);
      toast.error('Error al eliminar el track');
    } finally {
      setDeleting(null);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.name.toLowerCase().endsWith('.gpx')) {
        toast.error('Solo se permiten archivos GPX');
        return;
      }
      setUploadForm(prev => ({ 
        ...prev, 
        file,
        name: prev.name || file.name.replace('.gpx', '').replace(/_/g, ' ')
      }));
    }
  };

  const formatDuration = (minutes) => {
    if (!minutes) return '-';
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
  };

  // Share functions
  const openShareModal = (track) => {
    setShareTrack(track);
    setShowShareModal(true);
  };

  const handleCopyLink = async () => {
    if (!shareTrack?.gpx_file_url) return;
    try {
      await navigator.clipboard.writeText(shareTrack.gpx_file_url);
      setCopied(true);
      toast.success('Enlace copiado');
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error('Error al copiar');
    }
  };

  const handleShareWhatsApp = () => {
    if (!shareTrack) return;
    const text = `🏍️ Mi track "${shareTrack.name}"\n📏 ${shareTrack.distance_km?.toFixed(1) || '-'} km\n📅 ${shareTrack.date}\n\n${shareTrack.gpx_file_url || ''}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
  };

  const handleShareEmail = () => {
    if (!shareTrack) return;
    const subject = `Track: ${shareTrack.name}`;
    const body = `¡Hola!\n\nTe comparto mi track "${shareTrack.name}":\n\n📏 Distancia: ${shareTrack.distance_km?.toFixed(1) || '-'} km\n📅 Fecha: ${shareTrack.date}\n\nDescargar GPX: ${shareTrack.gpx_file_url || 'No disponible'}\n\n¡Un saludo!`;
    window.open(`mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`, '_blank');
  };

  const handleFollowTrack = (track) => {
    // Navigate to a follow/navigation page with the track
    // Store track in sessionStorage for the navigation page
    sessionStorage.setItem('followTrack', JSON.stringify({
      id: track.id,
      name: track.name,
      gpx_file_url: track.gpx_file_url
    }));
    window.location.href = `/follow-track/${track.id}`;
  };

  return (
    <div className="space-y-6 mt-6">
      {/* Track Recorder Section */}
      <div id="track-recorder">
        <TrackRecorder onTrackSaved={fetchTracks} />
      </div>
      
      {/* Tracks List */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between flex-wrap gap-2">
            <CardTitle className="flex items-center gap-2">
              <Route className="w-5 h-5 text-orange-500" />
              Mis Tracks ({tracks.length})
            </CardTitle>
            <Button 
              onClick={() => setShowUploadModal(true)}
              variant="outline"
              className="border-orange-500 text-orange-600 hover:bg-orange-50"
            >
              <Upload className="w-4 h-4 mr-2" />
              Subir GPX
            </Button>
          </div>
        </CardHeader>
      
      <CardContent>
        {loading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500 mx-auto"></div>
          </div>
        ) : tracks.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <MapPin className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p className="text-lg font-medium">No tienes tracks guardados</p>
            <p className="text-sm mt-1">Graba tu ruta o sube archivos GPX</p>
          </div>
        ) : (
          <div className="grid gap-4 md:grid-cols-2">
            {tracks.map((track) => (
              <div 
                key={track.id}
                className="border rounded-lg p-4 hover:shadow-md transition-shadow bg-white"
              >
                <div className="flex justify-between items-start mb-3">
                  <div className="flex-1 min-w-0">
                    <h4 className="font-semibold text-gray-900 truncate">{track.name}</h4>
                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                      <Badge variant={track.source === 'recorded' ? 'default' : 'secondary'}>
                        {track.source === 'recorded' ? 'Grabado' : 'Subido'}
                      </Badge>
                      {track.event_name && (
                        <Badge variant="outline" className="text-xs">
                          {track.event_name}
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>
                
                {/* Stats row */}
                <div className="grid grid-cols-2 gap-2 text-sm text-gray-600 mb-3">
                  <div className="flex items-center gap-1">
                    <Calendar className="w-3 h-3" />
                    {track.date}
                  </div>
                  {track.distance_km && (
                    <div className="flex items-center gap-1">
                      <TrendingUp className="w-3 h-3" />
                      {track.distance_km.toFixed(1)} km
                    </div>
                  )}
                  {track.duration_minutes && (
                    <div className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {formatDuration(track.duration_minutes)}
                    </div>
                  )}
                  {track.elevation_gain_m && (
                    <div className="flex items-center gap-1">
                      <Mountain className="w-3 h-3" />
                      +{track.elevation_gain_m}m
                    </div>
                  )}
                </div>
                
                {/* Action buttons */}
                <div className="flex gap-1 pt-2 border-t flex-wrap">
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => viewTrack(track)}
                    title="Ver track"
                  >
                    <Eye className="w-4 h-4 mr-1" />
                    <span className="hidden sm:inline">Ver</span>
                  </Button>
                  
                  {track.gpx_file_url && (
                    <>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => handleFollowTrack(track)}
                        className="text-green-600 hover:text-green-700 hover:bg-green-50"
                        title="Seguir este track"
                      >
                        <Navigation className="w-4 h-4 mr-1" />
                        <span className="hidden sm:inline">Seguir</span>
                      </Button>
                      
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => openShareModal(track)}
                        className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                        title="Compartir"
                      >
                        <Share2 className="w-4 h-4 mr-1" />
                        <span className="hidden sm:inline">Compartir</span>
                      </Button>
                    </>
                  )}
                  
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => deleteTrack(track.id)}
                    disabled={deleting === track.id}
                    className="text-red-500 hover:text-red-700 hover:bg-red-50 ml-auto"
                    title="Eliminar"
                  >
                    {deleting === track.id ? (
                      <div className="w-4 h-4 border-2 border-red-400 border-t-transparent rounded-full animate-spin"></div>
                    ) : (
                      <Trash2 className="w-4 h-4" />
                    )}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>

      {/* Upload Modal */}
      <Dialog open={showUploadModal} onOpenChange={setShowUploadModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="w-5 h-5" />
              Subir Track GPX
            </DialogTitle>
            <DialogDescription>
              Sube un archivo GPX de tu track favorito
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div>
              <Label htmlFor="gpx_file">Archivo GPX *</Label>
              <Input
                id="gpx_file"
                type="file"
                accept=".gpx"
                ref={fileInputRef}
                onChange={handleFileSelect}
                className="mt-1"
              />
            </div>
            
            <div>
              <Label htmlFor="track_name">Nombre del track *</Label>
              <Input
                id="track_name"
                value={uploadForm.name}
                onChange={(e) => setUploadForm(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Ej: Ruta Sierra de Gredos"
                className="mt-1"
              />
            </div>
            
            <div>
              <Label htmlFor="track_date">Fecha de realización</Label>
              <Input
                id="track_date"
                type="date"
                value={uploadForm.date}
                onChange={(e) => setUploadForm(prev => ({ ...prev, date: e.target.value }))}
                className="mt-1"
              />
            </div>
            
            <div>
              <Label htmlFor="track_description">Descripción (opcional)</Label>
              <Input
                id="track_description"
                value={uploadForm.description}
                onChange={(e) => setUploadForm(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Notas sobre la ruta..."
                className="mt-1"
              />
            </div>
            
            <div className="flex gap-2 pt-4">
              <Button
                variant="outline"
                onClick={() => setShowUploadModal(false)}
                className="flex-1"
              >
                Cancelar
              </Button>
              <Button
                onClick={uploadTrack}
                disabled={uploading || !uploadForm.name || !uploadForm.file}
                className="flex-1 bg-orange-500 hover:bg-orange-600"
              >
                {uploading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                    Subiendo...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-2" />
                    Guardar Track
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* View Track Modal */}
      <Dialog open={showTrackModal} onOpenChange={setShowTrackModal}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MapPin className="w-5 h-5 text-orange-500" />
              {selectedTrack?.name || 'Track'}
            </DialogTitle>
          </DialogHeader>
          
          {loadingTrack ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-orange-500 mx-auto"></div>
              <p className="text-gray-500 mt-3">Cargando track...</p>
            </div>
          ) : selectedTrack ? (
            <div className="space-y-4">
              {/* Stats */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                <div className="bg-gray-50 rounded-lg p-3 text-center">
                  <Calendar className="w-5 h-5 mx-auto text-orange-500 mb-1" />
                  <p className="text-xs text-gray-500">Fecha</p>
                  <p className="font-semibold">{selectedTrack.date}</p>
                </div>
                {selectedTrack.distance_km && (
                  <div className="bg-gray-50 rounded-lg p-3 text-center">
                    <TrendingUp className="w-5 h-5 mx-auto text-orange-500 mb-1" />
                    <p className="text-xs text-gray-500">Distancia</p>
                    <p className="font-semibold">{selectedTrack.distance_km.toFixed(1)} km</p>
                  </div>
                )}
                {selectedTrack.duration_minutes && (
                  <div className="bg-gray-50 rounded-lg p-3 text-center">
                    <Clock className="w-5 h-5 mx-auto text-orange-500 mb-1" />
                    <p className="text-xs text-gray-500">Duración</p>
                    <p className="font-semibold">{formatDuration(selectedTrack.duration_minutes)}</p>
                  </div>
                )}
                {selectedTrack.avg_speed_kmh && (
                  <div className="bg-gray-50 rounded-lg p-3 text-center">
                    <Gauge className="w-5 h-5 mx-auto text-orange-500 mb-1" />
                    <p className="text-xs text-gray-500">Vel. Media</p>
                    <p className="font-semibold">{selectedTrack.avg_speed_kmh} km/h</p>
                  </div>
                )}
                {selectedTrack.elevation_gain_m && (
                  <div className="bg-gray-50 rounded-lg p-3 text-center">
                    <Mountain className="w-5 h-5 mx-auto text-orange-500 mb-1" />
                    <p className="text-xs text-gray-500">Desnivel +</p>
                    <p className="font-semibold">{selectedTrack.elevation_gain_m}m</p>
                  </div>
                )}
              </div>
              
              {selectedTrack.description && (
                <p className="text-gray-600 text-sm bg-gray-50 p-3 rounded-lg">
                  {selectedTrack.description}
                </p>
              )}
              
              {/* Map */}
              {selectedTrack.track_points && selectedTrack.track_points.length > 0 && (
                <div className="h-96 rounded-lg overflow-hidden border">
                  <MapContainer
                    center={[selectedTrack.track_points[0].lat, selectedTrack.track_points[0].lon]}
                    zoom={12}
                    style={{ height: '100%', width: '100%' }}
                  >
                    <DynamicTileLayer />
                    <Polyline
                      positions={selectedTrack.track_points.map(p => [p.lat, p.lon])}
                      color="#f97316"
                      weight={4}
                    />
                    <FitBounds positions={selectedTrack.track_points} />
                  </MapContainer>
                </div>
              )}
              
              {/* Actions */}
              <div className="flex justify-end gap-2 pt-4 border-t">
                {selectedTrack.gpx_file_url && (
                  <Button
                    variant="outline"
                    onClick={() => window.open(selectedTrack.gpx_file_url, '_blank')}
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Descargar GPX
                  </Button>
                )}
                <Button
                  variant="outline"
                  onClick={() => setShowTrackModal(false)}
                >
                  Cerrar
                </Button>
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      {/* Share Modal */}
      <Dialog open={showShareModal} onOpenChange={setShowShareModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Share2 className="w-5 h-5 text-blue-600" />
              Compartir Track
            </DialogTitle>
            <DialogDescription>
              {shareTrack?.name}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-3 mt-4">
            <div className="grid grid-cols-2 gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  if (shareTrack?.gpx_file_url) {
                    window.open(shareTrack.gpx_file_url, '_blank');
                    toast.success('Descargando GPX...');
                  }
                }}
                className="flex flex-col items-center py-4 h-auto"
              >
                <Download className="w-5 h-5 mb-1 text-green-600" />
                <span className="text-xs">Descargar GPX</span>
              </Button>
              
              <Button
                variant="outline"
                onClick={handleCopyLink}
                className="flex flex-col items-center py-4 h-auto"
              >
                {copied ? (
                  <Check className="w-5 h-5 mb-1 text-green-600" />
                ) : (
                  <Copy className="w-5 h-5 mb-1 text-gray-600" />
                )}
                <span className="text-xs">{copied ? 'Copiado!' : 'Copiar enlace'}</span>
              </Button>
              
              <Button
                variant="outline"
                onClick={handleShareWhatsApp}
                className="flex flex-col items-center py-4 h-auto"
              >
                <MessageCircle className="w-5 h-5 mb-1 text-green-500" />
                <span className="text-xs">WhatsApp</span>
              </Button>
              
              <Button
                variant="outline"
                onClick={handleShareEmail}
                className="flex flex-col items-center py-4 h-auto"
              >
                <Mail className="w-5 h-5 mb-1 text-blue-600" />
                <span className="text-xs">Email</span>
              </Button>
            </div>
            
            <Button
              onClick={() => setShowShareModal(false)}
              className="w-full mt-2"
            >
              Cerrar
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </Card>
    </div>
  );
}
